/**
 * Plausible Analytics – privacy-safe event tracking.
 * No form values, free-text messages, email addresses, exact user IDs or raw dynamic URLs are sent.
 */

const isDev = import.meta.env.DEV;
const TRACKED_QUERY_PARAMS = ['tab', 'ref', 'source', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const SENSITIVE_KEY = /(email|mail|phone|address|message|description|content|body|password|token|secret|query|search|name|title|text)/i;
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s()./-]{7,}\d)/g;

export const CONSENT_KEY = 'schleishare_analytics_consent';

export const hasConsent = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(CONSENT_KEY) === 'granted';
};

export const normalizeRoute = (pathname = '/') => {
  const path = pathname || '/';
  const patterns = [
    [/^\/listing\/[^/]+$/, '/listing/:id'],
    [/^\/provider\/[^/]+$/, '/provider/:id'],
    [/^\/profile\/[^/]+$/, '/profile/:id'],
    [/^\/chat\/[^/]+$/, '/chat/:id'],
    [/^\/edit\/[^/]+$/, '/edit/:id'],
  ];
  return patterns.find(([pattern]) => pattern.test(path))?.[1] || path;
};

export const buildAnalyticsUrl = (input = typeof window !== 'undefined' ? window.location.href : 'https://schleishare.de/') => {
  try {
    const url = new URL(input, 'https://schleishare.de');
    const safeUrl = new URL(url.origin);
    safeUrl.pathname = normalizeRoute(url.pathname);

    TRACKED_QUERY_PARAMS.forEach((key) => {
      const value = url.searchParams.get(key);
      if (value && value.length <= 80) safeUrl.searchParams.set(key, value);
    });

    return safeUrl.toString();
  } catch {
    return 'https://schleishare.de/';
  }
};

const sanitizeString = (value, maxLength = 100) => String(value ?? '')
  .replace(EMAIL_PATTERN, '[email]')
  .replace(PHONE_PATTERN, '[phone]')
  .replace(UUID_PATTERN, ':id')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, maxLength);

const sanitizeValue = (key, value) => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'boolean' || typeof value === 'number') return value;
  if (Array.isArray(value)) return value.slice(0, 10).map((item) => sanitizeString(item, 50)).join(',');
  if (SENSITIVE_KEY.test(key)) {
    const text = sanitizeString(value, 80);
    return text ? `[redacted:${Math.min(text.length, 80)}]` : undefined;
  }
  return sanitizeString(value, 100);
};

export const sanitizeProps = (props = {}) => Object.entries(props).reduce((result, [key, value]) => {
  const sanitized = sanitizeValue(key, value);
  if (sanitized !== undefined) result[key] = sanitized;
  return result;
}, {});

export const safeInteractionLabel = (value) => {
  const label = sanitizeString(value, 70);
  if (!label || label.includes('[email]') || label.includes('[phone]')) return undefined;
  if (/^[0-9a-f-]{20,}$/i.test(label)) return undefined;
  return label;
};

export const initAnalytics = () => {
  if (typeof window === 'undefined' || !hasConsent()) return;

  window.plausible = window.plausible || function plausibleQueue() {
    (window.plausible.q = window.plausible.q || []).push(arguments);
  };
  window.plausible.init = window.plausible.init || function plausibleInit(options) {
    window.plausible.o = options || {};
  };

  window.plausible.init({
    autoCapturePageviews: false,
    outboundLinks: true,
    fileDownloads: true,
    formSubmissions: false,
  });

  if (document.getElementById('plausible-script')) return;
  const script = document.createElement('script');
  script.id = 'plausible-script';
  script.async = true;
  script.src = 'https://analytics.schleishare.de/js/pa-4pFBcgedYQhFjTOYRW6ox.js';
  document.head.appendChild(script);
};

if (hasConsent()) initAnalytics();

export const track = (eventName, props = {}) => {
  const safeProps = sanitizeProps({
    route: typeof window !== 'undefined' ? normalizeRoute(window.location.pathname) : '/',
    ...props,
  });

  if (isDev) console.log(`[Analytics] ${eventName}`, safeProps);
  if (!hasConsent()) return;

  if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
    window.plausible(eventName, {
      url: buildAnalyticsUrl(),
      props: safeProps,
    });
  }
};

export const trackPageview = (input, props = {}) => {
  if (!hasConsent()) return;
  if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
    window.plausible('pageview', {
      url: buildAnalyticsUrl(input),
      props: sanitizeProps(props),
    });
  }
};

// Listings
export const trackListingView = (props) => track('Listing:View', props);
export const trackListingCreate = (props) => track('Listing:Create', props);
export const trackListingUpdate = (props) => track('Listing:Update', props);
export const trackListingDelete = (props) => track('Listing:Delete', props);
export const trackListingMarkGone = (props) => track('Listing:MarkGone', props);
export const trackListingShare = (props) => track('Listing:Share', props);

// Chat
export const trackChatStart = (props) => track('Chat:Start', props);
export const trackChatMessage = (props) => track('Chat:Message', props);
export const trackChatReserve = (props) => track('Chat:Reserve', props);
export const trackChatReview = (props) => track('Chat:Review', props);
export const trackCommercialReview = (props) => track('Commercial:Review', props);

// Auth
export const trackLogin = (props) => track('Auth:Login', props);
export const trackRegister = (props) => track('Auth:Register', props);
export const trackLogout = () => track('Auth:Logout');

// Profile
export const trackProfileUpdate = (props) => track('Profile:Update', props);
export const trackProfileView = (props) => track('Profile:View', props);
export const trackProfileComplete = (props) => track('Profile:Complete', props);

// Feed
export const trackFeedTab = (props) => track('Feed:Tab', props);
export const trackFeedFilter = (props) => track('Feed:Filter', props);
export const trackFeedMap = (props) => track('Feed:Map', props);
export const trackFeedSort = (props) => track('Feed:Sort', props);
export const trackFeedSearch = (props = {}) => track('Feed:Search', {
  active: Boolean(props.query || props.search || props.active),
  result_count: props.result_count,
});

// Brochures
export const trackBrochureClick = (props) => track('Brochure:Click', {
  destination: props.destination || props.store || 'external-brochure',
});

// PWA
export const trackPWAInstall = () => track('PWA:Install');

// CTA / Navigation
export const trackCTAClick = (props) => track('CTA:Click', props);
export const trackNavClick = (props) => track('Nav:Click', props);
export const trackSharePlatform = (props) => track('Share:Platform', props);

// Menu
export const trackMenuItemClick = (props) => track('Menu:ItemClick', props);
